import { get } from "@spark/services/http";
import { getMensagensAcompanhamento, updateMensagem, deleteMensagem, createMensagem, addAnexoAcompanhamento } from "@spark/services/acompanhamentos";
import React, { useState, useEffect, useCallback } from 'react';
import { Card as BootstrapCard, Form, Button, Modal } from 'react-bootstrap';
import { Body, Caption } from '@spark/ui-components/components/Typography';
import Icon from '@spark/ui-components/components/Icon';
import FilePreview from '@spark/ui-components/components/FilePreview';
import { notification } from '@spark/ui-components/utils/notification';
const API_URL = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3500';
import { colorTextAdditional, colorBorder, colorHover } from '@spark/ui-components/theme';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import styled from 'styled-components';
import { useAuth } from '@/context/AuthContext';
import MensagemForm from './MensagemForm';
import SimpleLoader from '@spark/ui-components/components/SimpleLoader';

const MensagemList = ({
  acompanhamentoId
}) => {
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [editingMensagem, setEditingMensagem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editConteudo, setEditConteudo] = useState('');
  const [showAnexoModal, setShowAnexoModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const [selectedMensagem, setSelectedMensagem] = useState(null);
  const {
    user
  } = useAuth(); // user contém dados do Cognito (profile.sub é o ID)

  // Configuração de filtros

  const filterConfig = [{
    id: 'search',
    label: 'Buscar',
    type: 'text',
    placeholder: 'Buscar por conteúdo...'
  }, {
    id: 'usuario',
    label: 'Usuário',
    type: 'text',
    placeholder: 'Filtrar por usuário...'
  }, {
    id: 'dataInicio',
    label: 'Data Início',
    type: 'date'
  }, {
    id: 'dataFim',
    label: 'Data Fim',
    type: 'date'
  }];
  const loadMensagens = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (pagination.pageIndex > 0) params.append('page', pagination.pageIndex + 1);
      if (pagination.pageSize !== 10) params.append('limit', pagination.pageSize); // Adicionar filtros

      if (filters.search) params.append('search', filters.search);
      if (filters.usuario) params.append('usuario', filters.usuario);
      if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
      if (filters.dataFim) params.append('dataFim', filters.dataFim);
      const response = await getMensagensAcompanhamento(acompanhamentoId);

      if (response?.data?.mensagens) {
        setMensagens(response.data.mensagens);
      } else if (response?.data && Array.isArray(response.data)) {
        setMensagens(response.data);
      } else {
        setMensagens([]);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setLoading(false);
    }
  }, [acompanhamentoId, pagination, filters]);
  useEffect(() => {
    if (acompanhamentoId) {
      loadMensagens();
    }
  }, [acompanhamentoId, loadMensagens]);
  useEffect(() => {
    if (editingMensagem) {
      const conteudo = editingMensagem.Conteudo || editingMensagem.conteudo || '';
      setEditConteudo(conteudo);
    }
  }, [editingMensagem]);

  const handleFilterChange = newFilters => {
    setFilters(newFilters);
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize
    });
  };

  const handlePaginationChange = newPagination => {
    setPagination(newPagination);
  };

  const handleEditMensagem = mensagem => {
    setEditingMensagem(mensagem);
    setShowEditModal(true);
  };

  const handleDeleteMensagem = async mensagem => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) {
      return;
    }

    try {
      await deleteMensagem(acompanhamentoId, mensagem.Id);
      notification.success('Mensagem excluída com sucesso!');
      loadMensagens();
    } catch (error) {
      console.error('Erro ao excluir mensagem:', error); // Erro tratado automaticamente pelo interceptor global do axios
    }
  };

  const handleEditSubmit = async () => {
    if (!editConteudo.trim()) {
      notification.error('Digite uma mensagem');
      return;
    }

    try {
      setEditLoading(true);
      await updateMensagem(acompanhamentoId, editingMensagem.Id, {
        conteudo: editConteudo.trim()
      });
      notification.success('Mensagem atualizada com sucesso!');
      setShowEditModal(false);
      setEditingMensagem(null);
      setEditConteudo('');
      loadMensagens();
    } catch (error) {
      console.error('Erro ao atualizar mensagem:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setEditLoading(false);
    }
  };

  const handleFileSelect = event => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleUploadAnexo = async () => {
    if (!selectedFile) {
      notification.error('Selecione um arquivo');
      return;
    }

    try {
      setUploadingAnexo(true);
      const formData = new FormData();
      formData.append('arquivo', selectedFile);
      formData.append('mensagemId', selectedMensagem.Id);
      await addAnexoAcompanhamento(acompanhamentoId, formData);
      notification.success('Anexo enviado com sucesso!');
      setShowAnexoModal(false);
      setSelectedFile(null);
      setSelectedMensagem(null);
      loadMensagens();
    } catch (error) {
      console.error('Erro ao enviar anexo:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleDownloadAnexo = async anexo => {
    try {
      // Para todos os tipos de arquivo, tentar abrir a URL
      if (anexo.UrlArquivo) {
        window.open(anexo.UrlArquivo, '_blank');
        return;
      } // Fallback para download via API (se necessário)


      const response = await get(`${API_URL}/acompanhamentos/${acompanhamentoId}/mensagens/${anexo.MensagemId}/anexos/${anexo.Id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', anexo.NomeOriginal);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
      notification.error('Erro ao abrir anexo');
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEditMensagem = mensagem => {
    // Verificar se user existe e tem profile.sub (ID do Cognito)
    if (!user || !user.profile?.sub) {
      console.log('❌ user ou sub não encontrado');
      return false;
    }

    // Verificar se a mensagem tem UsuarioId
    if (!mensagem.UsuarioId) {
      console.log('❌ mensagem.UsuarioId não encontrado:', mensagem);
      return false;
    }

    // Comparar IDs (convertendo para string para garantir comparação correta)
    const canEdit = String(mensagem.UsuarioId) === String(user.profile.sub);
    console.log('✅ Verificação de edição:', {
      mensagemUsuarioId: mensagem.UsuarioId,
      userSub: user.profile.sub,
      canEdit
    });
    return canEdit;
  };

  return <MensagemListContainer>
      <FilterPanel filters={filterConfig} onApply={handleFilterChange} loading={loading} title="Filtros de Mensagens" />
      

      <MensagensArea>
        {loading ? <LoadingCenter>
            <SimpleLoader widthOrHeight={48} />
            <span style={{
          marginTop: 8,
          color: '#6c757d'
        }}>Carregando mensagens...</span>
          </LoadingCenter> : !mensagens || mensagens.length === 0 ? <EmptyState>
            <Icon name="comment-alert-outline" size={48} color={colorTextAdditional} />
            <Body style={{
          color: colorTextAdditional
        }}>
              Nenhuma mensagem encontrada
            </Body>
          </EmptyState> : <MensagensContainer>
            {Array.isArray(mensagens) && mensagens.map((mensagem, index) => <MensagemCard key={mensagem.Id || `mensagem-${index}`}>
                <MensagemHeader>
                  <div>
                    <Body style={{
                fontWeight: '500'
              }}>
                      {mensagem.criadoPor?.nome || mensagem.UsuarioNome || 'Usuário'}
                    </Body>
                    <Caption style={{
                color: colorTextAdditional
              }}>
                      {formatDate(mensagem.DataCriacao)}
                    </Caption>
                  </div>
                  {canEditMensagem(mensagem) && <MensagemActions>
                      <ActionButton onClick={() => handleEditMensagem(mensagem)} title="Editar mensagem">
                  
                        <Icon name="edit" size={16} />
                      </ActionButton>
                      <ActionButton onClick={() => handleDeleteMensagem(mensagem)} title="Excluir mensagem" danger>
                  
                        <Icon name="delete" size={16} />
                      </ActionButton>
                    </MensagemActions>}
              
                </MensagemHeader>
                
                <MensagemContent>
                  <Body>
                    {mensagem.Conteudo}
                  </Body>
                </MensagemContent>
                
                {mensagem.anexos && mensagem.anexos.length > 0 && <MensagemAnexos>
                    <AnexosHeader>
                      <Caption style={{
                color: colorTextAdditional,
                fontWeight: '500'
              }}>
                      <Icon name="paperclip" size={14} /> Anexos ({mensagem.anexos.length})
                    </Caption>
                    </AnexosHeader>
                    <AnexosList>
                      {mensagem.anexos.map((anexo, anexoIndex) => <FilePreview key={anexoIndex} file={anexo} onDownload={handleDownloadAnexo} showRemove={false} compact={true} />)}

                
                    </AnexosList>
                  </MensagemAnexos>}
            
                
                {canEditMensagem(mensagem) && <MensagemFooter>
                    <Button variant="outline-secondary" size="sm" onClick={() => {
              setSelectedMensagem(mensagem);
              setShowAnexoModal(true);
            }}>
                
                      <Icon name="paperclip" size={14} />
                      Adicionar Anexo
                    </Button>
                  </MensagemFooter>}
            
              </MensagemCard>)}
          
          </MensagensContainer>}
        
      </MensagensArea>

      {
      /* Formulário para adicionar nova mensagem */
    }
      <MensagemForm acompanhamentoId={acompanhamentoId} onSuccess={() => {
      loadMensagens();
    }} />
      

      {
      /* Modal de Edição */
    }
      <Modal show={showEditModal} animation={false} onHide={() => {
      setShowEditModal(false);
      setEditingMensagem(null);
      setEditConteudo('');
    }} size="md" centered>
        
        <Modal.Header closeButton>
          <Modal.Title>Editar Mensagem</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingMensagem && <Form.Group className="mb-3">
              <Form.Label>Mensagem</Form.Label>
              <Form.Control as="textarea" rows={3} value={editConteudo} onChange={e => setEditConteudo(e.target.value)} placeholder="Digite sua mensagem..." required />
            
            </Form.Group>}
          
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
          setShowEditModal(false);
          setEditingMensagem(null);
          setEditConteudo('');
        }}>
            
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleEditSubmit} disabled={editLoading}>
            
            {editLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {
      /* Modal de Anexo */
    }
      <Modal show={showAnexoModal} onHide={() => {
      setShowAnexoModal(false);
      setSelectedFile(null);
      setSelectedMensagem(null);
    }} size="md" centered>
        
        <Modal.Header closeButton>
          <Modal.Title>Adicionar Anexo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Selecione o arquivo</Form.Label>
            <Form.Control type="file" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif" />
            
            <Form.Text className="text-muted">
              Formatos aceitos: PDF, DOC, DOCX, TXT, JPG, PNG, GIF, CSV, XLS, XLSX (máx. 10MB)
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
          setShowAnexoModal(false);
          setSelectedFile(null);
          setSelectedMensagem(null);
        }}>
            
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleUploadAnexo} disabled={uploadingAnexo || !selectedFile}>
            
            {uploadingAnexo ? 'Enviando...' : 'Enviar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </MensagemListContainer>;
};

export default MensagemList; // Estilos

const MensagemListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  gap: 10px;
`;
const MensagensArea = styled.div`
  position: relative;
  min-height: 120px;
`;
const LoadingCenter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 0;
`;
const MensagensContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 600px;
  overflow-y: auto;
`;
const MensagemCard = styled(BootstrapCard)`
  border: 1px solid ${colorBorder};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
`;
const MensagemHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${colorBorder};
  background-color: ${colorHover};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const MensagemActions = styled.div`
  display: flex;
  gap: 8px;
`;
const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  color: ${props => props.danger ? '#dc3545' : '#6c757d'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.danger ? 'rgba(220, 53, 69, 0.1)' : 'rgba(108, 117, 125, 0.1)'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.danger ? 'rgba(220, 53, 69, 0.25)' : 'rgba(108, 117, 125, 0.25)'};
  }
`;
const MensagemContent = styled.div`
  padding: 16px;
`;
const MensagemAnexos = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${colorBorder};
  background-color: ${colorHover};
`;
const AnexosHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;
const AnexosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const MensagemFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${colorBorder};
  background-color: ${colorHover};
  display: flex;
  justify-content: flex-end;
`;
